import { Button, Card, CardActionArea, CardContent, Divider, Typography } from '@mui/material'
import { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { BoundariesList } from './BoundariesList'
import { CreateBoundary } from './CreateBoundary'
import { EditBoundaryOverlay, EditBoundaryProps } from './EditBoundaryOverlay'
import { UploadBoundaryFile } from './UploadBoundaryFile'

type OverlayStates = 'initial' | 'creating' | 'loading' | 'details' | 'drawing' | 'selectArea'

interface MapLayerOverlayProps extends EditBoundaryProps {
  boundaryTouched: boolean
  clearSelectedFips: () => void
  clearSelectedFeatures: () => void
}

export const MapLayerOverlay = forwardRef<HTMLElement, MapLayerOverlayProps>(
  ({ boundaryTouched, ...detailsProps }, ref) => {
    const { t } = useTranslation('impact/MapLayerOverlay')
    const navigate = useNavigate()
    const [params, setParams] = useSearchParams({ step: 'initial' })

    const overlayStates: Record<OverlayStates, JSX.Element> = {
      initial: <BoundariesList />,
      creating: <CreateBoundary />,
      loading: <UploadBoundaryFile />,
      details: <EditBoundaryOverlay {...detailsProps} />,
      drawing: <></>,
      selectArea: (
        <>
          <CardContent sx={{ padding: 3 }}>
            <Typography variant="h6" sx={{ marginBottom: 1 }}>
              {t('inventorySelect')}
            </Typography>
            <Typography variant="subtitle2">{t('inventorySelectDescription')}</Typography>
          </CardContent>
          <Divider />
          <CardActionArea
            component="div"
            sx={{ padding: 2, display: 'flex', justifyContent: 'space-between' }}
            style={{ pointerEvents: 'none' }}
          >
            <Button
              onClick={() => {
                detailsProps.clearSelectedFips()
                navigate(-1)
              }}
              variant="text"
              color="secondary"
              style={{ pointerEvents: 'all' }}
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={() => setParams({ step: 'details', type: 'select' })}
              variant="outlined"
              disabled={!boundaryTouched}
              style={{ pointerEvents: 'all' }}
            >
              {t('continue')}
            </Button>
          </CardActionArea>
        </>
      ),
    }
    return (
      <Card
        style={{ pointerEvents: 'all' }}
        sx={{
          borderRadius: 1.5,
          width: { xs: '100%', md: '50%' },
          maxWidth: params.get('step') === 'details' ? '460px' : '320px',
        }}
        // @ts-ignore forwarded ref can be used here, but mui is incorrectly typed to say it cannot
        ref={ref}
      >
        {overlayStates[params.get('step') as OverlayStates]}
      </Card>
    )
  }
)
